import React from "react";

/* ── THEME ── */
export const T = {
  bg: "#0b1f3a",
  panel: "#112b4d",
  panel2: "#15365f",
  border: "rgba(255,255,255,0.08)",
  text: "#edf4ff",
  muted: "#a8bddc",
  blue: "#44a6ff",
  green: "#53c46d",
  yellow: "#f3b53f",
  orange: "#ff7a45",
  red: "#e14d57",
  // A NEW HUE, and the only one added since this palette was set —
  // OPERATOR-APPROVED EXCEPTION to the existing-palettes-only rule
  // (2026-08-13), for the Readiness "Reschedule" pill. It needed a channel
  // no other readiness state already owns: green/yellow/orange/red are the
  // scan-staleness ramp and blue is "in review", so reusing any of them
  // would have made a rebook read as a stage of that ramp.
  //
  // Barbie pink, PULLED DOWN for this surface rather than taken neat.
  // The readiness pill derives its border and background from this one hex
  // (`${c}55`, `${c}1a`), so a fully saturated #FF69B4 glowed against the
  // #112b4d panel. #e8639b keeps the hue and drops the luminance to sit
  // beside T.red and T.orange at a comparable weight. Note the badge
  // palette's unused soft.pink is a FUCHSIA (232,121,249) — a different
  // hue and a different palette (Badge, not the readiness pill), so it is
  // a cousin here, not a substitute.
  pink: "#e8639b",
};

/* The two Badge palettes, at MODULE SCOPE (moved 2026-08-11).
   Previously locals inside Badge: re-created on every render, and reachable
   from nowhere else — so any other surface wanting a calibrated colour had
   to copy the values by hand, and no test could check that it had.
   Both are exported below for exactly that reason.

   PURE MOVE. Not one character of any colour value changed; the objects are
   byte-identical to their pre-move text apart from indentation. Every value
   here is already calibrated for the dark panel background and the
   rgba(255,255,255,0.05) row hover, and has been in production for months —
   which is why reusing an entry needs no visual review and inventing one
   does. */

// Heavy palette — severity pills (solid dark bg, pastel text, full-saturation border)
const heavy = {
  purple:  { bg:"#44256D", c:"#E9D5FF", border:"#8B5CF6" }, // critical
  red:     { bg:"#621520", c:"#FECACA", border:"#EF4444" }, // high
  orange:  { bg:"#7A3209", c:"#FFB66D", border:"#F97316" }, // medium
  yellow:  { bg:"#4F4715", c:"#FEF08A", border:"#FACC15" }, // low
  slate:   { bg:"#243047", c:"#94A3B8", border:"#475569" }, // informational
  green:   { bg:"#1f4d2a", c:"#bff5c8", border:"#53c46d" },
  // Teal — hue-matched to the soft palette's teal / DISPOSITION_COLORS
  // customer-managed teal (#14B8A6 family), built like its heavy siblings
  // (solid dark bg, pastel text, saturated border) so it holds contrast on
  // the panel background and the row-hover rgba(255,255,255,0.05).
  teal:    { bg:"#0d3d38", c:"#a7f3e7", border:"#14b8a6" },
  blue:    { bg:"#0a3961", c:"#bfe3ff", border:"#44a6ff" },
};
// Soft palette — status pills (rgba bg, pastel text, lighter border)
const soft = {
  blue:    { bg:"rgba(56,189,248,0.28)",  c:"#BAE6FD", border:"rgba(56,189,248,0.55)" },  // unresolved
  green:   { bg:"rgba(34,197,94,0.28)",   c:"#BBF7D0", border:"rgba(34,197,94,0.55)" },   // satisfied
  amber:   { bg:"rgba(245,158,11,0.30)",  c:"#FDE68A", border:"rgba(245,158,11,0.55)" },  // partial
  purple:  { bg:"rgba(168,85,247,0.28)",  c:"#DDD6FE", border:"rgba(168,85,247,0.55)" },  // risk accepted
  orange:  { bg:"rgba(251,146,60,0.28)",  c:"#FED7AA", border:"rgba(251,146,60,0.55)" },  // pending
  indigo:  { bg:"rgba(129,140,248,0.28)", c:"#C7D2FE", border:"rgba(129,140,248,0.55)" }, // planned
  pink:    { bg:"rgba(232,121,249,0.28)", c:"#F5D0FE", border:"rgba(232,121,249,0.55)" }, // (legacy — unused after CMS rename)
  teal:    { bg:"rgba(20,184,166,0.28)",  c:"#99F6E4", border:"rgba(20,184,166,0.55)" },  // customer managed solution
  yellow:  { bg:"rgba(243,181,63,0.28)",  c:"#fff1cb", border:"rgba(243,181,63,0.55)" },  // in-progress
  slate:   { bg:"rgba(148,163,184,0.20)", c:"#94A3B8", border:"rgba(148,163,184,0.45)" }, // deferred
};

/** EXPORTED FOR REUSE AND FOR TESTS. Descriptive aliases of the same
 *  objects — identity-equal, not copies, so a test can assert that another
 *  surface REUSED an entry rather than pasting its values. */
export { soft as BADGE_SOFT, heavy as BADGE_HEAVY };

/** The palette entry a Badge actually renders with, FALLBACK INCLUDED.
 *
 *  Extracted from Badge on 2026-08-13 so another surface can read the colour
 *  a badge is wearing rather than guess it — the Scheduling scan-method chips
 *  take their dot from here. The fallback is part of the answer and is why
 *  this is a function rather than an indexing expression: `amber` exists in
 *  `soft` and NOT in `heavy`, so a heavy amber badge renders BLUE today. That
 *  is pre-existing behaviour, untouched here; the chips reproduce whatever
 *  the cell shows, including this. */
export const badgeColors = (type, weight = "heavy") => {
  const palette = weight === "soft" ? soft : heavy;
  return palette[type] || palette.blue || heavy.blue;
};

export const Badge = ({ type, weight = "heavy", children }) => {
  const s = badgeColors(type, weight);
  return (
    <span style={{
      display:"inline-block",
      padding:"3px 10px",
      borderRadius:999,
      fontWeight:500,
      fontSize:11,
      background:s.bg,
      color:s.c,
      border:`1px solid ${s.border}`,
      letterSpacing:"0.02em",
      whiteSpace:"nowrap",
    }}>{children}</span>
  );
};

export const Card = ({ title, children, style: x }) => (
  <div style={{ background:`linear-gradient(180deg,${T.panel},${T.panel2})`, border:`1px solid ${T.border}`, borderRadius:18, padding:20, boxShadow:"0 18px 40px rgba(0,0,0,0.28)", ...x }}>
    {title && <h3 style={{ margin:"0 0 14px", fontSize:15, color:"#fff", fontWeight:700 }}>{title}</h3>}
    {children}
  </div>
);

export const Btn = ({ children, pri, del, style: x, onClick, disabled }) => {
  let bg = "rgba(255,255,255,0.04)", bc = T.border, c = T.text;
  if (pri) { bg = "rgba(68,166,255,0.2)"; bc = "rgba(68,166,255,0.4)"; }
  if (del) { c = T.red; bc = "rgba(225,77,87,0.3)"; }
  return <button onClick={onClick} disabled={disabled} style={{ padding:"8px 14px", borderRadius:10, border:`1px solid ${bc}`, background:bg, color:c, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1, fontFamily:"inherit", fontSize:12, fontWeight:600, transition:".15s", ...x }}>{children}</button>;
};

export const Modal = ({ show, title, onClose, onSubmit, submitLabel, submitDisabled, footer, children }) => {
  if (!show) return null;
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", justifyContent:"center", alignItems:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:T.panel, border:`1px solid ${T.border}`, borderRadius:18, padding:24, maxWidth:520, width:"92%", boxShadow:"0 12px 40px rgba(0,0,0,0.4)" }}>
        <h3 style={{ fontSize:16, fontWeight:700, marginBottom:10 }}>{title}</h3>
        <div style={{ fontSize:13, color:T.muted, marginBottom:14 }}>{children}</div>
        {footer || (
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <Btn onClick={onClose}>Cancel</Btn>
            <Btn pri onClick={onSubmit} disabled={submitDisabled}>{submitLabel || "Submit"}</Btn>
          </div>
        )}
      </div>
    </div>
  );
};
